<script setup>
  import { ref, computed, watch, onMounted, useTemplateRef } from 'vue';
  import { useUserStore } from '@/stores/UserStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { useMutationObserver } from '@vueuse/core';
  import WindGraphsContainer from '@/components/WindGraphsContainer.vue';
  import ProForecastContainer from '@/components/ProForecastContainer.vue'; 
  import ForecastTableContainer from '@/components/ForecastTableContainer.vue';
  import ArchiveDataContent from '@/components/ArchiveDataContent.vue';
  import StatsContent from '@/components/StatsContent.vue';
  import NearbyTidesContent from '@/components/NearbyTidesContent.vue';
  import FavoritedMap from '@/components/FavoritedMap.vue';
  import SpotListFavorites from '@/components/SpotListFavorites.vue';
  import FavoriteSpotsMap from '@/components/FavoriteSpotsMap.vue';
  import FavoriteContainerSection from '@/components/FavoriteContainerSection.vue';
  import FavoriteContainerTypeFilter from '@/components/FavoriteContainerTypeFilter.vue';
  import DataContainerSection from '@/components/DataContainerSection.vue';
  import DataContainer from '@/components/DataContainer.vue';
  import SegmentedControl from '@/components/controls/SegmentedControl.vue';
  import SegmentedControlButton from '@/components/controls/SegmentedControlButton.vue';
  import ToolbarHeaderActionListBase from "@/components/ToolbarHeaderActionListBase.vue";
  import Icon from "@/components/icons/Icon.vue";
  import useSettingsGlobals from '@/composables/useSettingsGlobals'
  import ModalUserProfileManager from '@/components/ModalUserProfileManager.vue';
  import ModalFavoriteContainerManager from '@/components/ModalFavoriteContainerManager.vue';
  import ModalFavoriteSpotManager from '@/components/ModalFavoriteSpotManager.vue';
  import SpotSortOrderSelector from '@/components/SpotSortOrderSelector.vue';
  import ModalCopyProfile from '@/components/ModalCopyProfile.vue';
  import ModalCopyContent from '@/components/ModalCopyContent.vue';
  import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
  import ToolbarHeaderActionMenuPopup from '@/components/ToolbarHeaderActionMenuPopup.vue';

  const PATHNAME = window.location.pathname;

  const { $PROFORECAST_ALLOWED } = useSettingsGlobals();

  const SAVEDCONTAINERTABS = {
    'Archive': 'archive-data',
    'Current Conditions': 'wind-graph',
    'Operational Forecast': 'pro-forecast',
    'Model Table': 'forecast-table',
    'Tides': 'nearby-tides',
    'Spot Stats': 'spot-stats',
    'Onsite Report': 'onsite-report',
    'Map': 'map'
  }

  const userStore = useUserStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const proForecastDataContainer = useTemplateRef('pro-forecast-date-container')

  await userStore.initialize();

  if (userStore.memberLevel >= 2) {
    await userStore.getProfiles();
  
    if (PATHNAME.indexOf('/favorites-lifestyle/') > -1) {
      const params = new URLSearchParams(window.location.search);
  
      userStore.setCurrentProfile(params.get('profile_id'));
    }
    
    if (PATHNAME.indexOf('/profile/') > -1) {
      userStore.setCurrentProfile(PATHNAME.split('/profile/')[1]);
    }  
  }

  const profileURL = window.location.href;

  const favoriteSpotsViewType = ref('list');
  const activeContainerTypes = ref([]);
  const showProfileManagerModal = ref(false);
  const showContainerManagerModal = ref(false);
  const showSpotManagerModal = ref(false);
  const showCopyProfileModal = ref(false);
  const showShareProfileModal = ref(false);
  const currentProfileId = ref(userStore.currentProfileId);
  const proForecastMinHeight = ref(userStore.memberLevel >= 49 && isDesktop ? '814px' : userStore.memberLevel >= 49 && !isDesktop ? ' 1000px' : '300px');
  const firstForecastTable = ref(null);
  
  const containerTypes = computed(() => {
    return new Set(userStore.profileAllContainers
      .map(container => container.container_type_id)
      .filter(containerType => {
        return !$PROFORECAST_ALLOWED && containerType === "Operational Forecast" ? false : true
      }))
  });
  const activeContainers = computed(() => userStore.profileAllContainers.filter(container => activeContainerTypes.value.has(container.container_type_id)));
  const favoriteSpots = computed(() => userStore.favoriteSpotData?.spots);
  const shareModalProfileModalTitle = computed(() => `Share Profile: ${userStore.currentProfile.name }`);

  activeContainerTypes.value = containerTypes.value;

  watch(currentProfileId, (newProfileId) => {
    userStore.setCurrentProfile(newProfileId);
  })

  const handleSortOrderChange = (sortOrder) => {
    googleAnalyticsStore.sendEvent("Windlist_Sort_Order", {
      "Hostname": window.location.hostname,
      "Pathname": window.location.pathname,
      "SortOrder": sortOrder,
    });
  }

  onMounted(() => {
    useMutationObserver(proForecastDataContainer, (mutations) => {
      if (firstForecastTable.value) return;

      firstForecastTable.value = document.querySelector('.jwx-pro-forecast__spot-forecast-block');
      
      if (firstForecastTable.value) {
        const dataContainerBoundingRect = mutations[0].target.closest('#pro-forecast').getBoundingClientRect();
        const forecastTableBoundingRect = firstForecastTable.value.getBoundingClientRect();
      
        proForecastMinHeight.value = `${forecastTableBoundingRect.top - dataContainerBoundingRect.top + (forecastTableBoundingRect.height * 2)}px`;
      }
        
    }, {
      childList: true,
      subtree: true,
    })
  });   
  
</script>

<template>
  <div class="jwx-page">
    <div class="jwx-page__container" data-section-gap="true">
      <header class="jwx-toolbar-header">
        <div class="jwx-toolbar-header__main-content jwx-page-block jwx-page-block--padding-sm">
          <div class="jwx-toolbar-header__title">
            <h2 class="jwx-toolbar-header__title-text">Favorites</h2>
            <div class="jwx-toolbar-header__subheader">
              <p class="jwx-toolbar-header__subheader-text">{{ userStore.currentProfile.name  }}</p>
            </div>
          </div>
          <ToolbarHeaderActionListBase v-if="isDesktop && userStore.memberLevel >= 2">
            <template #default="{ classes }">
              <li :class="classes.item">
                <button
                  @click="showProfileManagerModal = !showProfileManagerModal"
                  type="button"
                  :class="classes.button"
                >
                  <Icon name="user" :class="classes.icon" />
                  <span :class="classes.label">Profiles</span>
                </button>
              </li>
              <li :class="classes.item">
                <button
                  @click="showCopyProfileModal = !showCopyProfileModal"
                  type="button"
                  :class="classes.button"
                >
                  <Icon name="duplicate" :class="classes.icon" />
                  <span :class="classes.label">Duplicate</span>
                </button>
              </li>
              <li :class="classes.item">
                <button
                  @click="showShareProfileModal = !showShareProfileModal"
                  type="button"
                  :class="classes.button"
                >
                  <Icon name="share-arrow" :class="classes.icon" />
                  <span :class="classes.label">Share</span>
                </button>
              </li>
            </template>
          </ToolbarHeaderActionListBase>

          <ToolbarHeaderActionMenuPopup v-else-if="userStore.memberLevel >= 2">
            <template #menu-content>
              <ToolbarHeaderActionListBase>
                <template #default="{ classes }">
                  <li :class="classes.item">
                    <button
                      @click="showProfileManagerModal = !showProfileManagerModal"
                      type="button"
                      :class="classes.button"
                    >
                      <Icon name="user" :class="classes.icon" />
                      <span :class="classes.label">Profiles</span>
                    </button>
                  </li>
                  <li :class="classes.item">
                    <button
                      @click="showCopyProfileModal = !showCopyProfileModal"
                      type="button"
                      :class="classes.button"
                    >
                      <Icon name="duplicate" :class="classes.icon" />
                      <span :class="classes.label">Duplicate</span>
                    </button>
                  </li>
                  <li :class="classes.item">
                    <button
                      @click="showShareProfileModal = !showShareProfileModal"
                      type="button"
                      :class="classes.button"
                    >
                      <Icon name="share-arrow" :class="classes.icon" />
                      <span :class="classes.label">Share</span>
                    </button>
                  </li>
                </template>
              </ToolbarHeaderActionListBase>
            </template>
          </ToolbarHeaderActionMenuPopup>          
        </div>
      </header>
      <DataContainerSection
        id="favorite-spots-container"
        :sectionOverflow="false"
      >
        <template #title>Favorite Spots</template>
        <template #metaActions>
          <button
            v-if="favoriteSpots?.length > 0"
            @click.prevent="showSpotManagerModal = !showSpotManagerModal"
            type="button" 
            class="jwx-button jwx-button--pill jwx-button--small"
          >
            Manage
          </button>
        </template>

        <template #content>
          <DataContainer v-if="userStore.memberLevel >= 2">
            <div class="jwx-data-container__controls">
              <div class="jwx-control-bar">
                <SegmentedControl
                  v-model="favoriteSpotsViewType"
                  :disabled="favoriteSpots?.length === 0"
                  label="Display favorite spots as list or map."
                  style=" --segmented-control-background-color: var(--jwx-control-background-color-level-1)"
                >
                  <SegmentedControlButton value="list"><Icon name="list" /></SegmentedControlButton>
                  <SegmentedControlButton value="map"><Icon name="map" /></SegmentedControlButton>
                </SegmentedControl>
                <SpotSortOrderSelector
                  v-model="userStore.favoriteSortOrder"
                  id="favorite-spots-sort-order-selector"
                  @sortOrder:change="handleSortOrderChange"
                  :disabled="favoriteSpots?.length === 0"
                  :surface="0"
                />
              </div>
            </div>
            <div class="jwx-data-container__content">
              
              <SpotListFavorites
              v-if="favoriteSpotsViewType === 'list'"
              :key="userStore.currentProfileId + '-' + userStore.favoriteSortOrder"
              />
              
              <FavoriteSpotsMap
              v-else
              :profileId="userStore.currentProfileId"
              />

            </div>
          </DataContainer>
          <DataContainer v-else>
            <div lass="jwx-p-jumbo">
              <p class="jwx-text jwx-font-size-lg jwx-text-center jwx-m-jumbo">Please upgrade or create a free account to start saving favorites.</p>
            </div>
          </DataContainer>
        </template>
      </DataContainerSection>

      <header v-if="userStore.memberLevel >= 2" class="jwx-toolbar-header">
        <div class="jwx-toolbar-header__main-content">
          <div class="jwx-toolbar-header__title">
            <h2 class="jwx-toolbar-header__title-text">Favorite Containers</h2>
          </div>
          <div class="jwx-toolbar-header__actions">
            <FavoriteContainerTypeFilter
              v-model="activeContainerTypes"
              :containerTypes="containerTypes"
              :showTextLabel="isDesktop"
              :surface="0"
            />          
            <button
              v-if="userStore.profileAllContainers.length > 0"
              @click.prevent="showContainerManagerModal = !showContainerManagerModal"
              type="button" 
              class="jwx-button jwx-button--pill jwx-button--small"
              :data-surface="0"
            >
              Manage
            </button> 
          </div>
        </div>
      </header>

      <template v-if="userStore.memberLevel >= 2">
        <FavoriteContainerSection
            v-for="container in activeContainers"
            :favoriteContainer="container"
            minHeight="400px"
            :key="container.container_id"
          >
            <template #content="{ isVisible }">
              <DataContainer
                v-if="SAVEDCONTAINERTABS[container.container_type_id] === 'map' && isVisible"
              >
                <FavoritedMap
                  :containerSettings="JSON.parse(container.options)"
                  :mapName="container.container_name"
                  :containerId="container.container_id"
                />
              </DataContainer>
  
              <WindGraphsContainer
                v-else-if="SAVEDCONTAINERTABS[container.container_type_id] === 'wind-graph' && isVisible"
                :containerSettings="JSON.parse(container.options)"
                :showCurrentConditions="true"
                :showLastUpdateTimestamp="true"
                :windGraphType="container.options?.type || 'basic'"
                :isFavorite="true"
                class="jwx-page-block--padding-sm"
              />
              <DataContainer
                v-else-if="SAVEDCONTAINERTABS[container.container_type_id] === 'pro-forecast'  && $PROFORECAST_ALLOWED && isVisible"
                ref="pro-forecast-data-container"
                style="width: 100%; height: 100%;"
                contentType="pro-forecast"
              >
                <ProForecastContainer 
                  :memberLevel="userStore.memberLevel"
                  :collapsible="true"
                  :containerSettings="JSON.parse(container.options)"
                  :isFavorite="true"
                  :containerId="container.container_id"
                  :minHeight="proForecastMinHeight"
                />
              </DataContainer>
              <ForecastTableContainer 
                v-else-if="SAVEDCONTAINERTABS[container.container_type_id] === 'forecast-table' && isVisible"
                :containerSettings="JSON.parse(container.options)"
                :containerId="container.container_id"
                :isFavorite="true"
              />
              <DataContainer
                v-else-if="SAVEDCONTAINERTABS[container.container_type_id] === 'nearby-tides' && isVisible"
                style="width: 100%; height: 100%;"
                contentType="tides"
              >
                <NearbyTidesContent
                  :containerSettings="JSON.parse(container.options)" 
                  :isFavorite="true"   
                  :containerId="container.container_id"
                  :graphHeight="300"
                />              
              </DataContainer>
              <DataContainer
                v-else-if="SAVEDCONTAINERTABS[container.container_type_id] === 'archive-data' && isVisible"
                style="width: 100%; height: 100%;"
                contentType="archive-data"
              >
                <ArchiveDataContent 
                  :containerSettings="JSON.parse(container.options)"
                  :isFavorite="true"
                />
              </DataContainer>
              <DataContainer
                v-else-if="SAVEDCONTAINERTABS[container.container_type_id] === 'spot-stats' && isVisible"
                style="width: 100%; height: 100%;"
                contentType="stats"
              >
                <StatsContent
                  :spotId="JSON.parse(container.options).spotId"
                />
              </DataContainer>
            </template>
        </FavoriteContainerSection>
        <p v-if="userStore.profileAllContainers.length === 0" class="jwx-text jwx-font-size-lg jwx-text-center jwx-p-jumbo">There are no favorited containers to display.</p>
      </template>
    </div>
    <ModalUserProfileManager
      v-if="showProfileManagerModal"
      v-model:currentProfileId="currentProfileId"
      :userProfiles="userStore.userProfiles.profiles"
      :show="showProfileManagerModal"
      @close="showProfileManagerModal = false"
    />
    <ModalFavoriteContainerManager
      v-if="showContainerManagerModal"
      :show="showContainerManagerModal"
      :containers="activeContainers"
      :favoriteSpotContainerId="userStore.favoriteSpotContainers[currentProfileId].container_id"
      @close="showContainerManagerModal = false"
    />
    <ModalFavoriteSpotManager
      v-if="showSpotManagerModal"
      :spots="favoriteSpots"
      :show="showSpotManagerModal"
      @close="showSpotManagerModal = false"
    />
    <ModalCopyProfile 
      v-if="showCopyProfileModal"
      :show="showCopyProfileModal"      
      @close="showCopyProfileModal = false"
    />
    <ModalCopyContent 
      v-if="showShareProfileModal"
      :title="shareModalProfileModalTitle"
      :content="profileURL"
      :show="showShareProfileModal"      
      @close="showShareProfileModal = false"
    />
  </div>
  <Suspense>
    <ModalSiteSettings :show="userStore.showSettingsModal" />
  </Suspense>  
</template>